import { useState } from 'react';

import { Button } from '@components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@components/ui/dropdown-menu';
import { Skeleton } from '@components/ui/skeleton';
import { cn } from '@lib/ui/cn';
import { type Note } from '@models/Note';
import { type IDeleteFolderInputData } from '@services/folders/dtos/deleteFolder/InputData';
import { type IUpdateFolderInputData } from '@services/folders/dtos/updateFolder/InputData';
import {
  EllipsisVertical,
  Folder as FolderIcon,
  FolderInput,
  FolderPen,
  LoaderCircle,
  Palette,
  Square,
  Trash2,
} from 'lucide-react';
import { Link, useParams } from 'react-router';

import DeleteAlertDialog from '../../DeleteAlertDialog';
import { type IOpenMoveFolderInputData } from '../../MoveDialog';

interface IProps extends Note {
  isLoading: boolean;
  onDelete: (data: IDeleteFolderInputData) => Promise<void>;
  onOpenMoveDialog: (inputData: IOpenMoveFolderInputData) => void;
  onOpenRenameDialog: (data: Note) => void;
  onUpdate: (data: IUpdateFolderInputData) => Promise<void>;
}

const colorOptions = [
  { class: 'fill-[#EC313D] text-[#EC313D]', color: '#EC313D' },
  { class: 'fill-[#EC8031] text-[#EC8031]', color: '#EC8031' },
  { class: 'fill-[#ECC331] text-[#ECC331]', color: '#ECC331' },
  { class: 'fill-[#35EC31] text-[#35EC31]', color: '#35EC31' },
  { class: 'fill-[#31EC9E] text-[#31EC9E]', color: '#31EC9E' },
  { class: 'fill-[#31A9EC] text-[#31A9EC]', color: '#31A9EC' },
  { class: 'fill-[#3144EC] text-[#3144EC]', color: '#3144EC' },
  { class: 'fill-[#A531EC] text-[#A531EC]', color: '#A531EC' },
  { class: 'fill-[#EC31D9] text-[#EC31D9]', color: '#EC31D9' },
  { class: 'fill-[#EC3174] text-[#EC3174]', color: '#EC3174' },
  { class: 'fill-[#11824C] text-[#11824C]', color: '#11824C' },
  { class: 'fill-[#0B0964] text-[#0B0964]', color: '#0B0964' },
  { class: 'fill-[#69082B] text-[#69082B]', color: '#69082B' },
  { class: 'fill-[#776B6F] text-[#776B6F]', color: '#776B6F' },
  { class: 'fill-[#C0B5B9] text-[#C0B5B9]', color: '#C0B5B9' },
  { class: 'fill-[#211F54] text-[#211F54]', color: '#211F54' },
];

function Folder({
  category,
  color,
  id,
  isLoading,
  onDelete,
  onOpenMoveDialog,
  onOpenRenameDialog,
  onUpdate,
  title,
}: IProps) {
  const params = useParams();
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const handleUpdateColor = (color: string) => {
    onUpdate({
      color,
      id,
      title,
    });
  };

  return (
    <div className="border-primary bg-background text-primary shadow-foreground/50 hover:shadow-foreground/60 flex items-center justify-center gap-2 rounded-lg border-2 text-base font-bold shadow-md transition-all duration-300 hover:scale-[1.02] lg:text-lg">
      <Link
        className="flex flex-1 py-3 pl-3 lg:py-5 lg:pl-5"
        state={{
          label: name,
          path: `/anotacoes/${id}`,
        }}
        to={`/anotacoes/${id}`}>
        <div className="flex flex-1 flex-row items-center gap-1">
          {isLoading ? (
            <LoaderCircle className="text-tertiary size-8 animate-spin" />
          ) : (
            <FolderIcon
              className={cn(
                'size-9',
                colorOptions.find((item) => item.color === color)?.class,
              )}
              strokeWidth={1.5}
            />
          )}
          {isLoading ? (
            <Skeleton className={`h-4 w-[180px]`} />
          ) : (
            <p className="text-wrap">{title}</p>
          )}
        </div>
      </Link>
      <DropdownMenu onOpenChange={setMenuIsOpen} open={menuIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button className="cursor-pointer" size="icon" variant="ghost">
            <EllipsisVertical className="text-primary" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuGroup>
            <DropdownMenuItem
              className="focus:bg-tertiary"
              onClick={(e) => {
                e.stopPropagation();
                setMenuIsOpen(false);
                onOpenRenameDialog({
                  color,
                  id,
                  title,
                  category,
                });
              }}
              onSelect={(e) => e.preventDefault()}>
              <FolderPen className="size-6" strokeWidth={1.5} />
              Renomear pasta
            </DropdownMenuItem>

            <DropdownMenuItem
              className="focus:bg-tertiary"
              onClick={(e) => {
                e.stopPropagation();
                setMenuIsOpen(false);
                onOpenMoveDialog({
                  category,
                  title,
                  id,
                  folderOriginId: params.id ? params.id : null,
                });
              }}
              onSelect={(e) => e.preventDefault()}>
              <FolderInput className="size-6" strokeWidth={1.5} />
              Mover pasta
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="focus-within:bg-tertiary focus:bg-tertiary focus:text-background data-[state=open]:bg-tertiary data-[state=open]:text-background">
                <Palette className="mr-2 size-6" strokeWidth={1.5} />
                Alterar cor
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="grid grid-cols-[repeat(3,auto)]">
                  {colorOptions.map((item) => (
                    <DropdownMenuItem
                      className="focus:bg-tertiary/10 hover:cursor-pointer6"
                      key={item.color}
                      onClick={() => handleUpdateColor(item.color)}>
                      <Square
                        className={cn('size-6 hover:scale-[1.04]', item.class)}
                      />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuSeparator />

            <DeleteAlertDialog id={id} onDeleteFolder={onDelete}>
              <DropdownMenuItem
                className="text-destructive focus:bg-destructive focus:text-background"
                onClick={(e) => e.stopPropagation()}
                onSelect={(e) => e.preventDefault()}>
                <Trash2 className="text-destructive size-6" strokeWidth={1.5} />
                Excluir
              </DropdownMenuItem>
            </DeleteAlertDialog>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default Folder;
